import {
    Box,
    Button,
    Dialog,
    IconButton,
    InputAdornment,
    Skeleton,
    TextField,
    Theme,
    Typography
} from "@mui/material";
import CustomTable from "../common/customTable";
import MutualFundTableBody from "../common/customTable/MutualFundTableBody";
import {
    useEffect,
    useRef,
    useState
} from "react";
import {
    Order,
    PaginationObj,
    SortingObj,
    TableBodyData,
    TableHeaderData
} from "../types";
import { useDebounce } from "../../../utils/useDebounce";
import {
    GetSearchSchemesTableDataResponseBody,
    SelectedSchemesObj
} from "../../../redux-store/types/investorProfile";
import {
    getSearchSchemesTableData,
    setExploreMutualFundsPageVisited,
    setSelectedSchemesObj
} from "../../../redux-store/actions/investorProfile";
import {
    useDispatch,
    useSelector
} from "react-redux";
import {
    DialogCloseIcon,
    SearchIcon
} from "../../../assets/SvgIcons";
import { useHistory } from "react-router";
import { RootStateType } from "../../../redux-store/reducers";
import FiveSchemesDialog from "./FiveSchemesDialog";
import { whiteSpaceOnlyRegex } from "../../../utils/regex";
import { MAX_SCHEMES_SELECTED } from "../../../utils/constant";

const tableHeaderData: TableHeaderData[] = [
    {
        id: "schemeName",
        label: "SCHEME",
        align: "left",
        isSortable: true
    },
    {
        id: "year1Return",
        label: "1Y Returns",
        align: "center",
        isSortable: true
    },
    {
        id: "year3Return",
        label: "3Y Returns",
        align: "center",
        isSortable: true
    },
    {
        id: "year5Return",
        label: "5Y Returns",
        align: "center",
        isSortable: true
    },
]

const categories = ["All", "Equity", "Debt", "Hybrid", "Commodities"]

const categoriesMap: { [key: string]: string } = {
    "All": "",
    "Equity": "1",
    "Debt": "2",
    "Hybrid": "3",
    "Commodities": "4"
}

export default function SearchSchemesDialog({
    isVisible,
    onClose,
    handleProceedClick,
    clientCode
}: {
    isVisible: boolean;
    onClose: () => void;
    handleProceedClick: () => void;
    clientCode: string
}): JSX.Element {
    const dispatch = useDispatch()
    const history = useHistory()
    const { pathname: pathName } = history.location

    const {
        selectedSchemesObj = {}
    } = useSelector((store: RootStateType) => store.investorProfile);

    const [isFiveSchemesDialogVisible, setFiveSchemesDialogVisible] = useState<boolean>(false)
    const [isTableDataLoading, setTableDataLoading] = useState<boolean>(false)
    const [currentCategory, setCurrentCategory] = useState<string>("All");
    const [tableData, setTableData] = useState<TableBodyData[]>([])
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "asc",
        orderBy: "schemeName"
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })

    const { state } = history.location

    const { hideCheckBox = false } = state as { hideCheckBox: boolean }

    const isCheckBoxHidden = !!hideCheckBox;

    const [searchText, setSearchText] = useState<string>("")

    const previousSelectedSchemes = useRef<SelectedSchemesObj>(selectedSchemesObj)

    const debounceSearchText = useDebounce(searchText, 500);

    const fetchTableData = async ({
        searchInput,
        category,
        currentPage,
        fundsPerPage,
        sort,
        order
    }: {
        searchInput: string;
        category: string;
        currentPage: number;
        fundsPerPage: number;
        sort: string;
        order: Order
    }) => {
        let modifiedSearchText = ""

        if (searchInput?.trim()?.length >= 3) {
            modifiedSearchText = searchInput?.trim() ?? ""
        }

        const payload = {
            searchInput: modifiedSearchText,
            category,
            currentPage,
            fundsPerPage,
            sort,
            order
        }

        setTableDataLoading(true)

        const {
            totalLength = 0,
            currentPageFunds = []
        } = await (dispatch(getSearchSchemesTableData(payload)) as unknown as Promise<GetSearchSchemesTableDataResponseBody>)

        const modifiedTableData = currentPageFunds.map(obj => (obj.scheme in selectedSchemesObj) ?
            {
                ...obj,
                scheme: `${obj.scheme}`,
                checked: true
            } : {
                ...obj,
                scheme: `${obj.scheme}`,
                checked: false
            })

        setTableData(modifiedTableData)
        setTableSortingObj({
            order,
            orderBy: sort
        })
        setTablePaginationObj({
            currentPage: currentPage - 1,
            rowsPerPage: fundsPerPage,
            totalRows: totalLength
        })

    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                if ((debounceSearchText.trim().length === 0 || debounceSearchText.trim().length >= 3)
                    && !whiteSpaceOnlyRegex.test(debounceSearchText)) {
                    await fetchTableData({
                        searchInput: debounceSearchText,
                        category: categoriesMap[currentCategory],
                        currentPage: 1,
                        fundsPerPage: 5,
                        sort: tableSortingObj.orderBy,
                        order: tableSortingObj.order
                    })
                }
            } catch (e) {
                console.log("Error:", e)
            } finally {
                setTableDataLoading(false)
            }
        }

        fetchData()
    }, [debounceSearchText])

    const handleCategoryChange = async (currentCategory: string) => {
        try {
            setCurrentCategory(currentCategory)

            await fetchTableData({
                searchInput: debounceSearchText,
                category: categoriesMap[currentCategory],
                currentPage: 1,
                fundsPerPage: 5,
                sort: tableSortingObj.orderBy,
                order: tableSortingObj.order
            })
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setTableDataLoading(false)
        }
    }

    const handleTableSort = async (orderBy: string, order: Order) => {
        try {
            await fetchTableData({
                searchInput: debounceSearchText,
                category: categoriesMap[currentCategory],
                currentPage: 1,
                fundsPerPage: tablePaginationObj.rowsPerPage,
                sort: orderBy,
                order
            })
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setTableDataLoading(false)
        }
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            await fetchTableData({
                searchInput: debounceSearchText,
                category: categoriesMap[currentCategory],
                currentPage: 1,
                fundsPerPage: Number(event.target.value),
                sort: tableSortingObj.orderBy,
                order: tableSortingObj.order
            })
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setTableDataLoading(false)
        }
    }

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            await fetchTableData({
                searchInput: debounceSearchText,
                category: categoriesMap[currentCategory],
                currentPage: page + 1,
                fundsPerPage: tablePaginationObj.rowsPerPage,
                sort: tableSortingObj.orderBy,
                order: tableSortingObj.order
            })
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setTableDataLoading(false)
        }
    }

    const handleSearchSchemesDialogClose = () => {
        dispatch(setSelectedSchemesObj(previousSelectedSchemes.current))

        onClose()
    }

    const handleFiveSchemesDialogClose = () => {
        setFiveSchemesDialogVisible(false)
    }

    const handleSchemeClick = (schemeObj: TableBodyData) => {
        handleSearchSchemesDialogClose()

        dispatch(setExploreMutualFundsPageVisited(true))

        dispatch(setSelectedSchemesObj({
            [schemeObj.scheme]: { ...schemeObj, checked: true }
        }))

        history.push(`${pathName}/${schemeObj.scheme}`, { selectedSchemes: [schemeObj], clientCode: clientCode })
    }

    const handleCheckboxClick = (schemeId: string, checked: boolean) => {
        const isSelected = schemeId in selectedSchemesObj;

        if (Object.keys(selectedSchemesObj).length === MAX_SCHEMES_SELECTED && checked) {
            setFiveSchemesDialogVisible(true)
            return;
        } else {
            if (isSelected && !checked) {
                const { [schemeId]: _, ...modifiedSelectedSchemesObj } = selectedSchemesObj;
                dispatch(setSelectedSchemesObj(modifiedSelectedSchemesObj))
            }

            if (!isSelected && checked) {
                const modifiedSelectedSchemesObj = {
                    ...selectedSchemesObj,
                    [schemeId]: {
                        ...tableData
                            .filter((obj: TableBodyData) => obj.scheme === schemeId)
                            .map((obj: TableBodyData) => ({ ...obj, checked: true }))[0]
                    }
                }
                dispatch(setSelectedSchemesObj(modifiedSelectedSchemesObj))
            }

            setTableData((prevState: TableBodyData[]) => prevState
                .map((obj: TableBodyData) => {
                    return obj.scheme === schemeId ? { ...obj, checked } : obj
                }))
        }
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()

    }


    return (
        <>
            <Dialog
                onClose={handleSearchSchemesDialogClose}
                open={isVisible}
                maxWidth='lg'
                fullWidth
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '25px',
                        backgroundColor: theme.palette.common.white,
                        padding: "15px 25px 20px 25px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        maxHeight: "80%"
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px"
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: "center",
                            width: "100%"
                        }}>
                        <Typography
                            variant="h3"
                            sx={(theme: Theme) => ({
                                lineHeight: "22.5px",
                                color: theme.palette.text.primary
                            })}
                        >
                            All Schemes
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "20px"
                            }}
                        >
                            <Button
                                variant="contained"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.textDark.primary,
                                    // lineHeight: "22.5px",
                                    letterSpacing: "0.02px",
                                    backgroundColor: theme.palette.primary[500],
                                    padding: "8px 46px",
                                    ...theme.typography.body_500
                                })}
                                onClick={handleProceedClick}
                                disabled={isTableDataLoading}
                            >
                                Proceed
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            marginBottom: "10px",
                            ml: 2
                        }}
                    >
                        <IconButton
                            aria-label="dialog-close"
                            onClick={handleSearchSchemesDialogClose}
                            sx={{ padding: "5px" }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <TextField
                    autoFocus
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    sx={{
                                        display: searchText.length > 0 ? 'inline-flex' : 'none',
                                    }}
                                    onMouseDown={handleClearSearch}
                                >
                                    <DialogCloseIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: (theme: Theme) => ({
                            borderRadius: '47px',
                            height: '37.17px',
                            border: `1px solid ${theme.palette.text.secondary}B2`,
                            '& fieldset': {
                                border: 'none',
                            },
                            '& input': {
                                paddingLeft: '8px',
                                ...theme.typography.body_500,
                                color: theme.palette.text.primary,
                                '::placeholder': {
                                    ...theme.typography.subtitle_400,
                                    color: theme.palette.text.secondary,
                                    opacity: 1,
                                },
                            },
                        }),
                    }}
                    placeholder="Search for Mutual Funds..."
                    value={searchText}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setSearchText(event.target.value);
                    }}
                />
                <Typography
                    variant="overline_600"
                    sx={(theme: Theme) => ({
                        color: theme.palette.grey[500],
                        marginTop: "5px",
                        marginLeft: "8px"
                    })}
                >
                    Minimum 3 characters required
                </Typography>
                < Box
                    sx={{
                        marginTop: "10px",
                        display: "flex",
                        gap: "10px"
                    }}
                >

                    {categories.map((category) => (
                        <Box
                            key={category}
                            sx={(theme: Theme) => ({
                                padding: "5px 15px",
                                borderRadius: "7px",
                                backgroundColor: currentCategory === category ? theme.palette.primary[500] : theme.palette.common.white,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                            })}
                            onClick={() => handleCategoryChange(category)}
                        >
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    color: currentCategory === category ? theme.palette.textDark.primary : theme.palette.text.secondary
                                })}
                            >
                                {category}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Box
                    sx={{
                        marginTop: "15px",
                        maxHeight: "538px",
                        overflowY: "auto"
                    }}
                >
                    <CustomTable
                        isLoading={isTableDataLoading}
                        tableBody={
                            <MutualFundTableBody
                                tableBodyData={tableData}
                                tableBodyRowCustomStyles={{
                                    color: 'red',
                                    '& td:first-of-type': {
                                        paddingLeft: "0px"
                                    },
                                }}
                                handleCheckboxClick={handleCheckboxClick}
                                handleSchemeClick={handleSchemeClick}
                            />
                        }
                        tableData={tableData}
                        tableHeaderData={tableHeaderData}
                        handleSort={handleTableSort}
                        tableHeadRowCustomStyles={{
                            '& th:first-of-type': {
                                paddingLeft: "0px"
                            }
                        }}

                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        loadingRowsNum={7}
                        tableDataCount={tablePaginationObj.totalRows}
                        rowsPerPage={tablePaginationObj.rowsPerPage}
                        rowsPerPageOptions={[5, 10, 15, 20]}
                        handleRowsPerPageChange={handleTableRowsPerPageChange}
                        handlePageChange={handleTablePageChange}
                        currentPage={tablePaginationObj.currentPage}
                        order={tableSortingObj.order}
                        orderBy={tableSortingObj.orderBy}
                    />
                </Box>
            </Dialog >
            <FiveSchemesDialog
                isVisible={isFiveSchemesDialogVisible}
                onClose={handleFiveSchemesDialogClose}
            />
        </>
    )
}